(ns ui.editors.data-input
    (:require
     [clojure.edn :as edn]
     [clojure.string :as string]
     [common.languages :as languages]
     [common.localization :as loc]
     [common.schema.entities.survey.survey-text-variables :as survey-text-variables]
     [common.schema.data-types :as data-types]
     [common.select-util :as csu]
     [common.util :as cu]
     [ui.components.ck-editor.view :as ck-editor]
     [ui.components.color.hex-color :as hex-color]
     [ui.components.date-picker.view :as date-picker]
     [ui.components.input.boolean :as boolean-input]
     [ui.components.input.checkbox :as checkbox]
     [ui.components.input.checkbox-group :as checkbox-group]
     [ui.components.input.currency :as currency-input]
     [ui.components.input.email :as email-input]
     [ui.components.input.file-input :as file-input]
     [ui.components.input.integer :as integer-input]
     [ui.components.input.multiplier :as multiplier-input]
     [ui.components.input.number :as number-input]
     [ui.components.input.password-input :as password-input]
     [ui.components.input.percentage :as percentage-input]
     [ui.components.input.phone :as phone-input]
     [ui.components.input.simple-text :as simple-text-input]
     [ui.components.input.textarea :as textarea]
     [ui.components.radio-group.view :as radio-group]
     [ui.components.rich-text.tiptap :as tiptap]
     [ui.components.tiptap.view :as mention-tiptap]
     [ui.components.select.multi-select :as multi-select]
     [ui.components.select.select :as select]
     [ui.components.toggle.view :as toggle]
     [ui.editors.open-search.view :as open-search]
     [ui.util :as uu]))

(defn parse-value
  [{:keys [data-type native-value number-format date-format option-value-lookup]}]
  (when-not (nil? native-value)
    (let [trimmed-native (cu/str-trim native-value)]
      (case data-type
        :data.type/text-simple trimmed-native
        :data.type/text trimmed-native
        :data.type/text-url trimmed-native
        :data.type/email trimmed-native
        :data.type/phone trimmed-native
        :data.type/number (if (number? native-value)
                            native-value
                            (number-input/parse-value trimmed-native number-format))
        :data.type/date (if (number? native-value)
                          native-value
                          (date-picker/parse-date trimmed-native date-format))
        :data.type/datetime (if (number? native-value)
                              native-value
                              (date-picker/parse-datetime trimmed-native date-format))
        :data.type/date-year (if (number? native-value)
                               native-value
                               (integer-input/parse-value trimmed-native number-format))
        :data.type/currency-value (if (number? native-value)
                                    native-value
                                    (number-input/parse-value trimmed-native number-format))
        :data.type/currency-symbol (csu/parse-value trimmed-native option-value-lookup false)
        :data.type/percentage (if (number? native-value)
                                native-value
                                (percentage-input/parse-value trimmed-native number-format))
        :data.type/multiplier (if (number? native-value)
                                native-value
                                (multiplier-input/parse-value trimmed-native number-format))
        :data.type/long (if (number? native-value)
                          native-value
                          (integer-input/parse-value trimmed-native number-format))
        :data.type/integer (if (number? native-value)
                             native-value
                             (integer-input/parse-value trimmed-native number-format))
        :data.type/text-multiline trimmed-native
        :data.type/text-richtext trimmed-native
        :data.type/boolean (csu/parse-value trimmed-native option-value-lookup false)
        :data.type/keyword (csu/parse-value trimmed-native option-value-lookup false)
        :data.type/keyword-multi (csu/parse-value trimmed-native option-value-lookup true)
        :data.type/ref-single (csu/parse-value native-value option-value-lookup false)
        :data.type/ref-multi (csu/parse-value native-value option-value-lookup true)
        :data.type/password trimmed-native))))

(defn mention-variable-ids
  [variable-source]
  (survey-text-variables/variable-ids-for-source variable-source))

(defn view
  [{:keys [editor-type data-type value on-change variable-source class] :as props}]
  (let [data-type (cond (keyword? data-type)
                        data-type
                        (number? data-type)
                        (data-types/data-type-by-number-id data-type)
                        (string? data-type)
                        (keyword data-type))]
    (case editor-type
      :editor.type/text-mention
      [mention-tiptap/view
       {:value        value
        :class        (uu/cm "min-h-[80px]" class)
        :trigger-char "{"
        :item-texts   (mention-variable-ids variable-source)
        :on-change    (fn [text _html _json]
                        (when (fn? on-change)
                          (on-change text)))}]
      :editor.type/richtext-mention
      [mention-tiptap/view
       {:value        value
        :class        (uu/cm "min-h-[200px]" class)
        :trigger-char "{"
        :item-texts   (mention-variable-ids variable-source)
        :on-change    (fn [_text html _json]
                        (when (fn? on-change)
                          (on-change html)))}]
      :editor.type/search (case data-type
                            :data.type/ref-single [open-search/single-value props]
                            :data.type/ref-multi [open-search/multi-value props]
                            [open-search/single-value props])
      :editor.type/toggle [toggle/toggle
                           (assoc props :checked? (boolean value)
                                  :on-change! on-change)]
      :editor.type/checkbox [checkbox/view props]
      :editor.type/checkboxes [checkbox-group/view props]
      :editor.type/select [select/view props]
      :editor.type/multi-select [multi-select/view props]
      :editor.type/currency-combo [currency-input/view props]
      :editor.type/radio [radio-group/view props]
      :editor.type/hex-color [hex-color/view props]
      :editor.type/file-simple [file-input/view props]
      (case data-type
        :data.type/boolean [boolean-input/view props]
        :data.type/calculated-number [number-input/view (assoc props :disabled? true)]
        :data.type/currency-symbol [select/view props]
        :data.type/currency-value [number-input/view props]
        :data.type/date [date-picker/view props]
        :data.type/date-year [integer-input/view (assoc props :no-thousand-separator? true)]
        :data.type/datetime [date-picker/view props]
        :data.type/email [email-input/view props]
        :data.type/file [file-input/upload-zone props]
        :data.type/integer [integer-input/view props]
        :data.type/keyword [select/view props]
        :data.type/keyword-multi [multi-select/view props]
        :data.type/long [integer-input/view props]
        :data.type/multiplier [multiplier-input/view props]
        :data.type/number [number-input/view props]
        :data.type/password [password-input/view props]
        :data.type/percentage [percentage-input/view props]
        :data.type/phone [phone-input/view props]
        :data.type/ref-single [select/view props]
        :data.type/ref-multi [multi-select/view props]
        :data.type/text [simple-text-input/view props]
        :data.type/text-url [simple-text-input/view props]
        :data.type/text-multiline [textarea/view props]
        :data.type/text-richtext [ck-editor/editor props]
        :data.type/text-simple [simple-text-input/view props]
        [:div (str "Data-input missing < " data-type)]))))

(defn option-label
  "Get the label for an option value. Returns a fallback message if option is not found
   (e.g., when a previously selected option was deleted from the question)."
  [value options lang]
  (let [keyword-value? (or (some->> options
                                    (map :value)
                                    (keep keyword?)
                                    (first))
                           (some->> options
                                    (map :key)
                                    (keep keyword?)
                                    (first)))
        number-value? (or (some->> options
                                   (map :value)
                                   (keep number?)
                                   (first))
                          (some->> options
                                   (map :key)
                                   (keep number?)
                                   (first)))
        value (cond (and keyword-value? (string? value))
                    (keyword value)
                    (and number-value? (string? value))
                    (edn/read-string value)
                    :else
                    value)
        option (->> options
                    (cu/filter-by :value value)
                    (first))
        resolved-label (if-let [translations (get option :translations)]
                         (or (get translations lang)
                             (get translations (languages/long->short lang))
                             (get translations :en))
                         (let [label (get option :label)]
                           (cond (string? label)
                                 label
                                 (map? label)
                                 (or (get label lang)
                                     (get label :ENG)))))]
    (if (and (nil? resolved-label) (some? value))
      (loc/tr :previous-value-option-removed nil lang)
      resolved-label)))

(defn formatted-display-string
  [{:keys [data-type value options lang number-format date-format min-decimals max-decimals]}]
  (let [data-type (cond (keyword? data-type)
                        data-type
                        (number? data-type)
                        (data-types/data-type-by-number-id data-type)
                        (string? data-type)
                        (keyword data-type))]
    (case data-type
      :data.type/text-simple value
      :data.type/text value
      :data.type/text-url value
      :data.type/email value
      :data.type/phone value
      :data.type/number (number-input/format-value {:min-decimals  (or min-decimals 0)
                                                    :number-format number-format
                                                    :max-decimals  (or max-decimals 2)
                                                    :multiplier    nil}
                                                   value)
      :data.type/date (date-picker/format-display-value value date-format)
      :data.type/datetime (date-picker/format-display-value value date-format)
      :data.type/date-year value
      :data.type/currency-value (number-input/format-value {:min-decimals  (or min-decimals 0)
                                                            :number-format number-format
                                                            :max-decimals  (or max-decimals 2)
                                                            :multiplier    nil}
                                                           value)
      :data.type/currency-symbol (option-label value options lang)
      :data.type/percentage (percentage-input/format-value {:number-format number-format}
                                                           value)
      :data.type/integer (number-input/format-value {:min-decimals  0
                                                     :number-format number-format
                                                     :max-decimals  0
                                                     :multiplier    nil}
                                                    value)
      :data.type/text-multiline (->> (string/split-lines value)
                                     (map (fn [line]
                                            [:p line]))
                                     (into [:div]))
      :data.type/text-richtext [tiptap/viewer {:value value}]
      :data.type/boolean (let [value (cond (boolean? value)
                                           value
                                           (string? value)
                                           (edn/read-string value)
                                           :else
                                           value)]
                           (cond (true? value)
                                 (loc/tr :yes nil lang)
                                 (false? value)
                                 (loc/tr :no nil lang)))
      :data.type/keyword (option-label value options lang)
      :data.type/keyword-multi (->> value
                                    (map (fn [v]
                                           (option-label v options lang)))
                                    (string/join ",\n "))
      :data.type/ref-single (option-label value options lang)
      :data.type/ref-multi (->> value
                                (map (fn [v]
                                       (option-label v options lang)))
                                (string/join ",\n "))
      (do
        (js/console.error "Unknown data-type in ui.editors.data-input/formatted-display-string [" data-type "]")
        nil))))
